import React from "react";
import { Box, Typography } from "@mui/material";
import { MilestoneTaskEstimateChangedAlert } from "../../features/alerts/MilestoneTaskEstimateChangedAlert";
import { PercentageCompleteChangedAlert as PercentageCompleteChangedAlertType } from "../../features/alerts/percentageCompleteChangedAlert/PercentageCompleteChangedAlert";
import TimeFormatter from "../../features/extensions/TimeFormatter";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PercentIcon from "@mui/icons-material/Percent";

interface TaskReportAlertProps {
  estimateAlerts: MilestoneTaskEstimateChangedAlert[];
  percentageAlerts: PercentageCompleteChangedAlertType[];
}

const TaskReportAlert: React.FC<TaskReportAlertProps> = ({
  estimateAlerts,
  percentageAlerts,
}) => {
  const hasEstimateAlerts = estimateAlerts.length > 0;
  const hasPercentageAlerts = percentageAlerts.length > 0;
  const timeFormatter = new TimeFormatter();

  if (!hasEstimateAlerts && !hasPercentageAlerts) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "rgba(255, 244, 229, 0.98)",
        border: "1px solid",
        borderColor: "warning.light",
        borderRadius: 1,
        boxShadow: (theme) => theme.shadows[2],
        maxWidth: "none",
        "& .MuiTypography-root": {
          color: "text.primary",
        },
      }}
    >
      {hasEstimateAlerts && (
        <Box sx={{ mb: hasPercentageAlerts ? 2 : 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "medium",
              mb: 1,
              color: "text.secondary",
            }}
          >
            Estimate Changes
          </Typography>
          {estimateAlerts.map((alert) => (
            <Box
              key={alert.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
                whiteSpace: "nowrap",
                width: "max-content",
              }}
            >
              <AccessTimeIcon fontSize="small" color="action" />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.primary",
                  "& .time": {
                    fontWeight: "medium",
                    color: "primary.main",
                  },
                  "& .date": {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "text.secondary",
                    ml: 0.5,
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    pl: 1,
                  },
                }}
              >
                <Typography variant="body2" component="span">
                  Estimate changed from
                </Typography>
                <Typography variant="body2" component="span" className="time">
                  {timeFormatter.toHoursAndMinutes(alert.from)}
                </Typography>
                <Typography variant="body2" component="span">
                  to
                </Typography>
                <Typography variant="body2" component="span" className="time">
                  {timeFormatter.toHoursAndMinutes(alert.to)}
                </Typography>
                <Box component="span" className="date">
                  <CalendarTodayIcon sx={{ fontSize: 14 }} />
                  <Typography variant="body2" component="span">
                    {new Date(alert.dateCreated).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {hasPercentageAlerts && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "medium",
              mb: 1,
              color: "text.secondary",
            }}
          >
            Percentage Complete Changes
          </Typography>
          {percentageAlerts.map((alert) => (
            <Box
              key={alert.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
                whiteSpace: "nowrap",
                width: "max-content",
              }}
            >
              <PercentIcon fontSize="small" color="action" />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.primary",
                  "& .percentage": {
                    fontWeight: "medium",
                    color: "primary.main",
                  },
                  "& .date": {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "text.secondary",
                    ml: 0.5,
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    pl: 1,
                  },
                }}
              >
                <Typography variant="body2" component="span">
                  Percentage complete changed from
                </Typography>
                <Typography variant="body2" component="span" className="percentage">
                  {alert.from}%
                </Typography>
                <Typography variant="body2" component="span">
                  to
                </Typography>
                <Typography variant="body2" component="span" className="percentage">
                  {alert.to}%
                </Typography>
                <Box component="span" className="date">
                  <CalendarTodayIcon sx={{ fontSize: 14 }} />
                  <Typography variant="body2" component="span">
                    {new Date(alert.dateCreated).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TaskReportAlert;
